// webapp/src/lib/netTap.ts
// Dev-only network tap for specific paths. Safe to ship; inert unless enabled.

type Matcher = string | RegExp;
type TapOptions = { logBodies?: boolean };

function match(url: string, m: Matcher): boolean {
  return typeof m === 'string' ? url.includes(m) : m.test(url);
}

export function installNetTap(matchers: Matcher[], opts: TapOptions = {}) {
  if (typeof window === 'undefined') return;
  if ((window as any).__NET_TAP_INSTALLED__) return;
  (window as any).__NET_TAP_INSTALLED__ = true;

  const shouldLog = (url: string) => matchers.some((m) => match(url, m));
  const log = (phase: string, data: Record<string, unknown>) =>
    console.warn(`[nettap] ${phase}`, data); // eslint-disable-line no-console

  // fetch()
  const _fetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = String(typeof input === 'string' ? input : (input as any).url);
    const t0 = performance.now();
    const id = Math.round(t0 * 1000);

    if (shouldLog(url)) {
      log('request', {
        id,
        url,
        method: (init?.method || 'GET').toUpperCase(),
        redirect: init?.redirect,
        credentials: init?.credentials,
      });
    }

    try {
      const res = await _fetch(input, init);
      if (shouldLog(url)) {
        const t1 = performance.now();
        const info: Record<string, unknown> = {
          id,
          url: res.url || url,
          ms: Math.round(t1 - t0),
          status: res.status,
          redirected: res.redirected,
          type: (res as any).type,
        };
        if (opts.logBodies && res.headers.get('content-type')?.includes('application/json')) {
          try { info.body = await res.clone().json(); } catch {}
        }
        log('response', info);
      }
      return res;
    } catch (err: any) {
      if (shouldLog(url)) log('error', { id, url, message: String(err?.message || err) });
      throw err;
    }
  };

  // navigator.sendBeacon()
  const _sb = navigator.sendBeacon?.bind(navigator);
  if (_sb) {
    (navigator as any).sendBeacon = (url: string | URL, data?: BodyInit | null) => {
      const u = String(url);
      const ok = _sb(u, data);
      if (shouldLog(u)) log('beacon', { url: u, ok, bytes: (data as any)?.length ?? null });
      return ok;
    };
  }
}
